import { ReactNode, useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Plus } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { handleError } from "@/lib/errorHandler";
import { FormulaEditor } from "./FormulaEditor";

interface FieldManagerProps {
  tableId: string;
  onFieldCreated: () => void;
  children?: ReactNode;
}

const FIELD_TYPES = [
  { value: "text", label: "Single Line Text" },
  { value: "long_text", label: "Long Text" },
  { value: "number", label: "Number" },
  { value: "currency", label: "Currency" },
  { value: "percent", label: "Percent" },
  { value: "single_select", label: "Single Select" },
  { value: "multi_select", label: "Multi Select" },
  { value: "checkbox", label: "Checkbox" },
  { value: "date", label: "Date" },
  { value: "email", label: "Email" },
  { value: "phone", label: "Phone" },
  { value: "url", label: "URL" },
  { value: "formula", label: "Formula" },
  { value: "link_to_record", label: "Link to Record" },
  { value: "lookup", label: "Lookup" },
  { value: "attachment", label: "Attachment" },
];

export const FieldManager = ({ tableId, onFieldCreated, children }: FieldManagerProps) => {
  const [open, setOpen] = useState(false);
  const [fieldName, setFieldName] = useState("");
  const [fieldType, setFieldType] = useState("text");
  const [creating, setCreating] = useState(false);
  const [showFormulaEditor, setShowFormulaEditor] = useState(false);
  
  // Link to record config
  const [targetTableId, setTargetTableId] = useState("");
  const [allowMultiple, setAllowMultiple] = useState(true);
  const [symmetricLink, setSymmetricLink] = useState(false);
  const [availableTables, setAvailableTables] = useState<any[]>([]);
  
  // Lookup config
  const [linkedFieldId, setLinkedFieldId] = useState("");
  const [lookupFieldId, setLookupFieldId] = useState("");
  const [linkedFields, setLinkedFields] = useState<any[]>([]);
  const [targetFields, setTargetFields] = useState<any[]>([]);
  
  // Select options
  const [selectOptions, setSelectOptions] = useState("");

  useEffect(() => {
    if (open) {
      fetchAvailableTables();
      fetchLinkedFields();
    }
  }, [open, tableId]);

  useEffect(() => {
    if (targetTableId) {
      fetchTargetFields();
    }
  }, [targetTableId]);

  const fetchAvailableTables = async () => {
    try {
      // Get the base_id from current table
      const { data: currentTable } = await supabase
        .from("tables")
        .select("base_id")
        .eq("id", tableId)
        .single();

      if (currentTable) {
        const { data: tables } = await supabase
          .from("tables")
          .select("id, name")
          .eq("base_id", currentTable.base_id)
          .neq("id", tableId);

        setAvailableTables(tables || []);
      }
    } catch (error) {
      console.error("Failed to fetch tables:", error);
    }
  };

  const fetchLinkedFields = async () => {
    try {
      const { data } = await supabase
        .from("fields")
        .select("id, name, config")
        .eq("table_id", tableId)
        .eq("type", "link_to_record");

      setLinkedFields(data || []);
    } catch (error) {
      console.error("Failed to fetch linked fields:", error);
    }
  };

  const fetchTargetFields = async () => {
    try {
      const { data } = await supabase
        .from("fields")
        .select("id, name, type")
        .eq("table_id", targetTableId);

      setTargetFields(data || []);
    } catch (error) {
      console.error("Failed to fetch target fields:", error);
    }
  };

  const handleCreate = async (e: React.FormEvent, formulaData?: { expression: string; type: string }) => {
    e.preventDefault();
    setCreating(true);

    try {
      const fieldData: any = {
        table_id: tableId,
        name: fieldName,
        type: fieldType as any,
        config: {}
      };

      // Handle formula fields
      if (fieldType === "formula" && formulaData) {
        fieldData.formula_expression = formulaData.expression;
        fieldData.formula_type = formulaData.type;
      }

      // Handle link_to_record fields
      if (fieldType === "link_to_record") {
        if (!targetTableId) {
          toast.error("Please select a target table");
          setCreating(false);
          return;
        }
        fieldData.config = {
          target_table_id: targetTableId,
          allow_multiple: allowMultiple,
          symmetric_link: symmetricLink,
        };
      }

      // Handle lookup fields
      if (fieldType === "lookup") {
        if (!linkedFieldId || !lookupFieldId) {
          toast.error("Please configure the lookup settings");
          setCreating(false);
          return;
        }
        fieldData.config = {
          linked_field_id: linkedFieldId,
          lookup_field_id: lookupFieldId,
        };
      }

      // Handle select fields
      if (fieldType === "single_select" || fieldType === "multi_select") {
        const options = selectOptions.split(",").map(o => o.trim()).filter(Boolean);
        fieldData.config = { options };
      }

      const { error } = await supabase
        .from("fields")
        .insert([fieldData]);

      if (error) throw error;

      // Create symmetric link field if enabled
      if (fieldType === "link_to_record" && symmetricLink) {
        const targetTable = availableTables.find(t => t.id === targetTableId);
        await supabase.from("fields").insert([{
          table_id: targetTableId,
          name: `${fieldName} (linked)`,
          type: "link_to_record" as any,
          config: {
            target_table_id: tableId,
            allow_multiple: allowMultiple,
            symmetric_link: true,
          }
        }]);
      }

      toast.success("Field created!");
      resetForm();
      setOpen(false);
      onFieldCreated();
    } catch (error: any) {
      handleError(error, "Failed to create field");
    } finally {
      setCreating(false);
    }
  };

  const resetForm = () => {
    setFieldName("");
    setFieldType("text");
    setTargetTableId("");
    setAllowMultiple(true);
    setSymmetricLink(false);
    setLinkedFieldId("");
    setLookupFieldId("");
    setSelectOptions("");
    setShowFormulaEditor(false);
  };

  const handleTypeChange = (value: string) => {
    setFieldType(value);
    if (value === "formula") {
      setShowFormulaEditor(true);
    }
  };

  const renderTypeSpecificConfig = () => {
    switch (fieldType) {
      case "link_to_record":
        return (
          <div className="space-y-4 pt-2 border-t">
            <div className="space-y-2">
              <Label>Link to Table</Label>
              <Select value={targetTableId} onValueChange={setTargetTableId}>
                <SelectTrigger>
                  <SelectValue placeholder="Select a table" />
                </SelectTrigger>
                <SelectContent>
                  {availableTables.map((table) => (
                    <SelectItem key={table.id} value={table.id}>
                      {table.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div className="flex items-center justify-between">
              <div className="space-y-0.5">
                <Label>Allow Multiple Links</Label>
                <p className="text-xs text-muted-foreground">Link to multiple records</p>
              </div>
              <Switch checked={allowMultiple} onCheckedChange={setAllowMultiple} />
            </div>
            
            <div className="flex items-center justify-between">
              <div className="space-y-0.5">
                <Label>Create Symmetric Link</Label>
                <p className="text-xs text-muted-foreground">Also create a link field in the target table</p>
              </div>
              <Switch checked={symmetricLink} onCheckedChange={setSymmetricLink} />
            </div>
          </div>
        );

      case "lookup":
        return (
          <div className="space-y-4 pt-2 border-t">
            <div className="space-y-2">
              <Label>Linked Field</Label>
              <Select value={linkedFieldId} onValueChange={(val) => {
                setLinkedFieldId(val);
                const linkedField = linkedFields.find(f => f.id === val);
                if (linkedField?.config?.target_table_id) {
                  setTargetTableId(linkedField.config.target_table_id);
                }
              }}>
                <SelectTrigger>
                  <SelectValue placeholder="Select a linked field" />
                </SelectTrigger>
                <SelectContent>
                  {linkedFields.map((field) => (
                    <SelectItem key={field.id} value={field.id}>
                      {field.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {linkedFields.length === 0 && (
                <p className="text-xs text-muted-foreground">
                  Create a "Link to Record" field first
                </p>
              )}
            </div>
            
            {linkedFieldId && (
              <div className="space-y-2">
                <Label>Field to Lookup</Label>
                <Select value={lookupFieldId} onValueChange={setLookupFieldId}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select a field" />
                  </SelectTrigger>
                  <SelectContent>
                    {targetFields.map((field) => (
                      <SelectItem key={field.id} value={field.id}>
                        {field.name} ({field.type})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
          </div>
        );

      case "single_select":
      case "multi_select":
        return (
          <div className="space-y-2 pt-2 border-t">
            <Label>Options (comma-separated)</Label>
            <Input
              value={selectOptions}
              onChange={(e) => setSelectOptions(e.target.value)}
              placeholder="Option 1, Option 2, Option 3"
            />
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <>
      <Dialog open={open} onOpenChange={(isOpen) => {
        setOpen(isOpen);
        if (!isOpen) resetForm();
      }}>
        <DialogTrigger asChild>
          {children || (
            <Button size="sm" variant="outline">
              <Plus className="h-4 w-4 mr-2" />
              Add Field
            </Button>
          )}
        </DialogTrigger>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Add Field</DialogTitle>
            <DialogDescription>
              Add a new column to your table
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={(e) => handleCreate(e)} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="field-name">Field Name</Label>
              <Input
                id="field-name"
                placeholder="e.g., Name, Status, Priority"
                value={fieldName}
                onChange={(e) => setFieldName(e.target.value)}
                required
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="field-type">Field Type</Label>
              <Select value={fieldType} onValueChange={handleTypeChange}>
                <SelectTrigger id="field-type">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {FIELD_TYPES.map((type) => (
                    <SelectItem key={type.value} value={type.value}>
                      {type.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            {renderTypeSpecificConfig()}
            
            {fieldType !== "formula" && (
              <Button type="submit" className="w-full" disabled={creating}>
                {creating ? "Creating..." : "Create Field"}
              </Button>
            )}
          </form>
        </DialogContent>
      </Dialog>

      {showFormulaEditor && (
        <FormulaEditor
          open={showFormulaEditor}
          onClose={() => {
            setShowFormulaEditor(false);
            setFieldType("text");
          }}
          onSave={(expression, type) => {
            handleCreate(new Event('submit') as any, { expression, type });
          }}
        />
      )}
    </>
  );
};
